import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router";
import Content from "../layout/content/Content";
import Head from "../layout/head/Head";
import {
  Block,
  BlockDes,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
  Row,
  Col,
  PreviewAltCard,
  TooltipComponent,
} from "../components/Component";
import {
  BalanceBarChart,
  DepositBarChart,
  WithdrawBarChart,
} from "../components/partials/charts/invest/InvestChart";
import PRSummaryWidget from "./dashboard/PRSummaryWidget";
import TopPurchaseCategoriesWidget from "./dashboard/TopPurchseCategoriesWidget";
import TopSupplierSavingsWidget from "./dashboard/TopSupplierSavingsWidget";
import BudgetOverRunWidget from "./dashboard/BudgetOverRunWidget";
import P2PWidget from "./dashboard/P2PWidget";
import PurchaseOrdersDrawer from "./dashboard/PurchaseOrdersDrawer";
import InvoicesDrawer from "./dashboard/InvoicesDrawer";
import AccountsPayablesDrawer from "./dashboard/AccountsPayablesDrawer";
import { formatCurrency2, formatCurrency } from "../utils/Utils";
import { Tooltip } from "antd"; // Import Tooltip from Ant Design
import { InfoCircleOutlined } from "@ant-design/icons"; // Import Info Icon from Ant Design
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { Select, DatePicker, Flex, Spin, Empty } from "antd";
import moment from "moment";
import mainDashboardApis from "../api/dashboard/MainDashboard";
import Swal from "sweetalert2";
import contractorsApis from "../api/master/contractors";
import supplierApis from "../api/master/supplier";

const { Option } = Select;
const { RangePicker } = DatePicker;

const InvestHomePage = () => {
  const location = useLocation();
  const { data: currentUser } = useSelector((state) => state.currentUser);
  const [sm, updateSm] = useState(false);
  const [site, setSite] = useState("");
  const [siteOptions, setSiteOptions] = useState([]);
  const [contractorOptions, setContractorOptions] = useState([]);
  const [contractor, setContractor] = useState("");
  const [dateRange, setDateRange] = useState("Quarter to Date");
  const [customDateRange, setCustomDateRange] = useState();

  const [filtersWrap, setFiltersWrap] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 699.98px)");
    const handleChange = (e) => setFiltersWrap(e.matches);
    handleChange(mql);
    if (mql.addEventListener) {
      mql.addEventListener("change", handleChange);
      return () => mql.removeEventListener("change", handleChange);
    }
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, []);

  const [topWidgetsData, setTopWidgetsData] = useState();
  const [topPurchaseCategoriesData, setTopPurchaseCategoriesData] = useState();
  const [procureToPayCycleData, setProcureToPayCycleData] = useState();
  const [purchaseRequestSummaryData, setPurchaseRequestSummaryData] =
    useState();
  const [loadingCount, setLoadingCount] = useState(4);
  const [purchaseOrdersDrawerVisible, setPurchaseOrdersDrawerVisible] =
    useState(false);
  const [invoicesDrawerVisible, setInvoicesDrawerVisible] = useState(false);
  const [accountsPayablesDrawerVisible, setAccountsPayablesDrawerVisible] =
    useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const isMountedRef = useRef(true);
  
  const convertToDates = (dateType) => {
    switch (dateType) {
      case "Month to Date":
        return {
          from: dayjs().startOf("month").format("YYYY-MM-DD"),
          to: dayjs().format("YYYY-MM-DD"),
        };
      case "Quarter to Date":
        return {
          from: dayjs()
            .subtract(3, "month")
            .startOf("month")
            .format("YYYY-MM-DD"),
          to: dayjs().format("YYYY-MM-DD"),
        };
      case "Year to Date":
        return {
          from: dayjs().startOf("year").format("YYYY-MM-DD"),
          to: dayjs().format("YYYY-MM-DD"),
        };
      default:
        return {
          from: dayjs(customDateRange?.[0]).format("YYYY-MM-DD"),
          to: dayjs(customDateRange?.[1]).format("YYYY-MM-DD"),
        };
    }
  };

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility && siteAccessibility.length > 0) {
        const allSites = [];
        const _siteOptions = [];
        siteAccessibility?.forEach((item) => {
          allSites.push(item.siteID);
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSite(allSites.join(","));
        setSiteOptions([
          {
            label: "All Sites",
            value: allSites?.join(","),
          },
          ..._siteOptions,
        ]);
      } else {
        // No sites configured for this user/org — stop the spinner so the empty state can render
        setSite("");
        setSiteOptions([]);
        setLoadingCount(0);
      }
    }
  }, [currentUser]);

  // Check location state to open drawer when navigating back from PO/Invoice view
  useEffect(() => {
    const previousState = location.state?.previousState;
    if (previousState?.openPurchaseOrdersDrawer) {
      setPurchaseOrdersDrawerVisible(true);
      // Restore filters if they were set
      if (previousState.site) {
        setSite(previousState.site);
      }
      if (previousState.contractor) {
        setContractor(previousState.contractor);
      }
      if (previousState.dateRange) {
        // Handle dateRange restoration if needed
      }
      if (previousState.categoryId) {
        // Handle categoryId if needed in the future
      }
    }
    if (previousState?.openInvoicesDrawer) {
      setInvoicesDrawerVisible(true);
      // Restore filters if they were set
      if (previousState.site) {
        setSite(previousState.site);
      }
      if (previousState.contractor) {
        setContractor(previousState.contractor);
      }
      if (previousState.dateRange) {
        // Handle dateRange restoration if needed
      }
      if (previousState.categoryId) {
        // Handle categoryId if needed in the future
      }
    }
    if (previousState?.openAccountsPayablesDrawer) {
      setAccountsPayablesDrawerVisible(true);
      // Restore filters if they were set
      if (previousState.site) {
        setSite(previousState.site);
      }
      if (previousState.contractor) {
        setContractor(previousState.contractor);
      }
      if (previousState.dateRange) {
        // Handle dateRange restoration if needed
      }
      if (previousState.categoryId) {
        // Handle categoryId if needed in the future
      }
    }
  }, [location.state]);

  const handleSiteChange = (value) => {
    setSite(value.toString());
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value !== "Custom Date") {
      setCustomDateRange(null);
    }
  };

  const handleCustomDateChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setCustomDateRange([
        moment(dates[0].toDate()),
        moment(dates[1].toDate()),
      ]);
    } else {
      setCustomDateRange(null);
    }
  };

  const fetchContractorList = async (siteId) => {
    try {
      const { data, status } = await contractorsApis.fetchContractorBySiteId({
        id: siteId + "",
      });
      if (status && Array.isArray(data) && data.length > 0) {
        let allContractors = [];
        let _contractorOptions = [];
        data.forEach((item) => {
          allContractors.push(item?.contractorID?.toString());
          _contractorOptions.push({
            label: item?.companyName,
            value: item?.contractorID?.toString(),
          });
        });

        setContractor(allContractors?.join(","));
        if (_contractorOptions.length > 1) {
          _contractorOptions = [
            {
              label: "ALL",
              value: allContractors?.join(","),
            },
            ..._contractorOptions,
          ];
        }
        setContractorOptions(_contractorOptions);
      } else {
        setContractor("");
        setContractorOptions([]);
      }
    } catch (error) {
      console.log(error);
      setContractor("");
      setContractorOptions([]);
    }
  };

  const fetchCategories = useCallback(async () => {
    try {
      const { data, status } = await supplierApis.fetchSupplierCategory();
      if (status) {
        const allCategory = [];
        const _categoryOptions = [];
        data?.forEach((item) => {
          allCategory.push(item.id);
          _categoryOptions.push({ label: item.category, value: item.id });
        });
        setCategoryOptions([
          {
            label: "All Categories",
            value: allCategory?.join(","),
          },
          ..._categoryOptions,
        ]);
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  const fetchTopWidgetsResponse = useCallback(async () => {
    try {
      const { from, to } = convertToDates(dateRange);
      const formData = {
        siteId: site,
        contractorId: contractor,
        status: ["Accepted"],
        invoiceType: ["commercial"],
        startDate: from,
        endDate: to,
      };
      const res = await mainDashboardApis.topWidgetsResponse(formData);
      const { response, status, message } = res;
      if (status) {
        setTopWidgetsData(response);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingCount((pre) => --pre);
    }
  }, [site, contractor, dateRange, customDateRange]);

  const fetchTopPurchaseCategories = useCallback(async () => {
    try {
      const { from, to } = convertToDates(dateRange);
      const formData = {
        siteId: site,
        contractorId: contractor,
        startDate: from,
        endDate: to,
      };
      const res = await mainDashboardApis.topPurchaseCategories(formData);
      const { response, status, message } = res;
      if (status) {
        setTopPurchaseCategoriesData(response);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingCount((pre) => --pre);
    }
  }, [site, contractor, dateRange, customDateRange]);

  const fetchProcureToPayCycle = useCallback(async () => {
    try {
      const { from, to } = convertToDates(dateRange);
      const formData = {
        siteId: site,
        contractorId: contractor,
        startDate: from,
        endDate: to,
      };
      const res = await mainDashboardApis.procureToPayCycle(formData);
      const { response, status, message } = res;
      if (status) {
        setProcureToPayCycleData(response);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingCount((pre) => --pre);
    }
  }, [site, contractor, dateRange, customDateRange]);

  const fetchPurchaseRequestSummary = useCallback(async () => {
    try {
      const { from, to } = convertToDates(dateRange);
      const formData = {
        siteId: site,
        contractorId: contractor,
        contractorId: contractor,
        startDate: from,
        endDate: to,
      };
      const res = await mainDashboardApis.purchaseRequestSummary(formData);
      const { response, status, message } = res;
      if (status) {
        setPurchaseRequestSummaryData(response);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingCount((pre) => --pre);
    }
  }, [site, contractor, dateRange, customDateRange]);

  const fetchAllDashboardData = useCallback(() => {
    if (!site || !isMountedRef.current) {
      setLoadingCount(0);
      return;
    }
    setLoadingCount(4);
    fetchTopWidgetsResponse();
    fetchTopPurchaseCategories();
    fetchPurchaseRequestSummary();
    fetchProcureToPayCycle();
  }, [fetchTopWidgetsResponse, fetchTopPurchaseCategories, fetchPurchaseRequestSummary, fetchProcureToPayCycle, site]);

  const [contractorLoaded, setContractorLoaded] = useState(false);

  // When site changes: fetch contractors first, then dashboard data loads via contractorLoaded change
  useEffect(() => {
    if (site) {
      setContractorLoaded(false);
      fetchContractorList(site).then(() => {
        setContractorLoaded(true);
      });
    }
  }, [site]);

  // Fetch dashboard data after contractor list is loaded or when filters change
  useEffect(() => {
    if (site && contractorLoaded) {
      fetchAllDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractorLoaded, contractor, dateRange, customDateRange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return (
    <React.Fragment>
      <Head title="Procurex Dashboard" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween
            className={filtersWrap ? "flex-wrap" : ""}
          >
            <BlockHeadContent>
              <BlockTitle page>Dashboard</BlockTitle>
              <BlockDes className="text-soft">
                <p>Welcome to Procurex.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent className={filtersWrap ? "w-100" : ""}>
              <div className="toggle-wrap nk-block-tools-toggle">
                {!filtersWrap && (
                  <Button
                    className={`btn-icon btn-trigger toggle-expand me-n1 ${
                      sm ? "active" : ""
                    }`}
                    onClick={() => updateSm(!sm)}
                  >
                    <Icon name="more-v"></Icon>
                  </Button>
                )}
                <div
                  className="toggle-expand-content"
                  style={{
                    display: filtersWrap || sm ? "block" : "none",
                    width: filtersWrap ? "100%" : undefined,
                    marginTop: filtersWrap ? 12 : undefined,
                  }}
                >
                  <ul className="nk-block-tools g-3">
                    <li
                      style={{
                        display: "flex",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        gap: "12px 16px",
                        overflowX: "auto",
                        maxWidth: "100%",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <label style={{ fontWeight: "bold", marginRight: 8, whiteSpace: "nowrap" }}>
                          Filter by Site:
                        </label>
                        <Select
                          defaultValue="ALL"
                          style={{ width: 150 }}
                          onChange={handleSiteChange}
                        >
                          {siteOptions?.map((item, idx) => (
                            <Option
                              key={"siteOption_" + idx}
                              value={item.value}
                            >
                              {item.label}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <label style={{ fontWeight: "bold", marginRight: 8, whiteSpace: "nowrap" }}>
                          Filter by Contractor:
                        </label>
                        <Select
                          value={contractor || undefined}
                          placeholder="ALL"
                          style={{ width: 150 }}
                          onChange={(value) => {
                            setContractor(value);
                          }}
                        >
                          {contractorOptions?.map((item) => (
                            <Option value={item.value}>{item.label}</Option>
                          ))}
                        </Select>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <label style={{ fontWeight: "bold", marginRight: 8, whiteSpace: "nowrap" }}>
                          Filter by Date Range:
                        </label>
                        <Select
                          defaultValue="Quarter to Date"
                          style={{ width: 200, marginRight: 16 }}
                          onChange={handleDateRangeChange}
                        >
                          <Option value="Month to Date">Month to Date</Option>
                          <Option value="Quarter to Date">
                            Quarter to Date
                          </Option>
                          <Option value="Year to Date">Year to Date</Option>
                          <Option value="Custom Date">Custom Date</Option>
                        </Select>
                        {dateRange === "Custom Date" && (
                          <RangePicker
                            value={
                              customDateRange
                                ? [
                                    dayjs(customDateRange[0].toDate()),
                                    dayjs(customDateRange[1].toDate()),
                                  ]
                                : null
                            }
                            onChange={(dates, dateStrings) =>
                              handleCustomDateChange(dates)
                            }
                            disabledDate={(current) =>
                              current && current > dayjs().endOf("day")
                            }
                            style={{ width: 300 }}
                          />
                        )}
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        {loadingCount > 0 ? (
          <>
            <Flex align="center" gap="middle" className="pageLoader">
              <Spin size="large" className="m-auto" />
            </Flex>
          </>
        ) : !site ? (
          <Block>
            <Empty
              description="No sites are mapped to your account yet. Please contact your administrator to configure site access."
              style={{ padding: "60px 0" }}
            />
          </Block>
        ) : (
          <Block>
            <Row className="g-gs">
              <Col md="4">
                <PreviewAltCard className="card-full">
                  <div className="card-title-group align-start mb-0">
                    <div className="card-title">
                      <h6 className="subtitle">Total Purchase Orders</h6>
                    </div>
                    <div className="card-tools" style={{ display: "flex", gap: "8px" }}>
                      <a
                        href="#"
                        style={{
                          marginLeft: "auto",
                          textAlign: "right",
                          // fontSize: "14px",
                          cursor: "pointer",
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          setPurchaseOrdersDrawerVisible(true);
                        }}
                      >
                        View Details
                      </a>
                      <TooltipComponent
                        iconClass="card-hint"
                        icon="help-fill"
                        direction="left"
                        id="invest-deposit"
                        text="Total Purchase Orders"
                      ></TooltipComponent>
                    </div>
                  </div>
                  <div className="card-amount">
                    <span className="amount">
                      {formatCurrency2(
                        topWidgetsData?.totalPurchaseOrders
                          ?.total_order_value || 0,
                        "INR"
                      )}
                      <Tooltip
                        title={formatCurrency(
                          topWidgetsData?.totalPurchaseOrders
                            ?.total_order_value,
                          "INR"
                        )}
                      >
                        <InfoCircleOutlined
                          style={{ marginLeft: 8, fontSize: 12 }}
                        />
                      </Tooltip>
                    </span>
                  </div>
                  <div className="invest-data">
                    <div className="invest-data-amount g-2">
                      <div className="invest-data-history">
                        <div className="title">This Month</div>
                        <span className="amount">
                          {formatCurrency2(
                            topWidgetsData?.totalPurchaseOrders
                              ?.this_monthOrder || 0,
                            "INR"
                          )}
                          <Tooltip
                            title={formatCurrency(
                              topWidgetsData?.totalPurchaseOrders
                                ?.this_monthOrder,
                              "INR"
                            )}
                          >
                            <InfoCircleOutlined
                              style={{ marginLeft: 8, fontSize: 12 }}
                            />
                          </Tooltip>
                        </span>
                      </div>
                      <div className="invest-data-history">
                        <div className="title">This Week</div>
                        <span className="amount">
                          {formatCurrency2(
                            topWidgetsData?.totalPurchaseOrders
                              ?.this_weekOrder || 0,
                            "INR"
                          )}
                          <Tooltip
                            title={formatCurrency(
                              topWidgetsData?.totalPurchaseOrders
                                ?.this_weekOrder,
                              "INR"
                            )}
                          >
                            <InfoCircleOutlined
                              style={{ marginLeft: 8, fontSize: 12 }}
                            />
                          </Tooltip>
                        </span>
                      </div>
                    </div>
                    <div className="invest-data-ck">
                      <DepositBarChart
                        totalPurchaseOrdersData={
                          topWidgetsData?.totalPurchaseOrders?.data
                        }
                      />
                    </div>
                  </div>
                </PreviewAltCard>
              </Col>

              <Col md="4">
                <PreviewAltCard className="card-full">
                  <div className="card-title-group align-start mb-0">
                    <div className="card-title">
                      <h6 className="subtitle">Total Invoiced Value</h6>
                    </div>
                    <div className="card-tools" style={{ display: "flex", gap: "8px" }}>
                      <a
                        href="#"
                        style={{
                          marginLeft: "auto",
                          textAlign: "right",
                          // fontSize: "14px",
                          cursor: "pointer",
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          setInvoicesDrawerVisible(true);
                        }}
                      >
                        View Details
                      </a>
                      <TooltipComponent
                        iconClass="card-hint"
                        icon="help-fill"
                        direction="left"
                        id="invest-withdraw"
                        text="Total Orders Received"
                      ></TooltipComponent>
                    </div>
                  </div>
                  <div className="card-amount">
                    <span className="amount">
                      {formatCurrency2(
                        topWidgetsData?.totalOrdersReceived
                          ?.total_invoice_value || 0,
                        "INR"
                      )}
                      <Tooltip
                        title={formatCurrency(
                          topWidgetsData?.totalOrdersReceived
                            ?.total_invoice_value,
                          "INR"
                        )}
                      >
                        <InfoCircleOutlined
                          style={{ marginLeft: 8, fontSize: 12 }}
                        />
                      </Tooltip>
                    </span>
                  </div>
                  <div className="invest-data">
                    <div className="invest-data-amount g-2">
                      <div className="invest-data-history">
                        <div className="title">This Month</div>
                        <div className="amount">
                          {formatCurrency2(
                            topWidgetsData?.totalOrdersReceived
                              ?.this_monthinvoice || 0,
                            "INR"
                          )}
                          <Tooltip
                            title={formatCurrency(
                              topWidgetsData?.totalOrdersReceived
                                ?.this_monthinvoice,
                              "INR"
                            )}
                          >
                            <InfoCircleOutlined
                              style={{ marginLeft: 8, fontSize: 12 }}
                            />
                          </Tooltip>
                        </div>
                      </div>
                      <div className="invest-data-history">
                        <div className="title">This Week</div>
                        <div className="amount">
                          {formatCurrency2(
                            topWidgetsData?.totalOrdersReceived
                              ?.this_weekinvoice || 0,
                            "INR"
                          )}
                          <Tooltip
                            title={formatCurrency(
                              topWidgetsData?.totalOrdersReceived
                                ?.this_weekinvoice,
                              "INR"
                            )}
                          >
                            <InfoCircleOutlined
                              style={{ marginLeft: 8, fontSize: 12 }}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                    <div className="invest-data-ck">
                      <WithdrawBarChart
                        withdrawBarChartData={
                          topWidgetsData?.totalOrdersReceived?.data
                        }
                      />
                    </div>
                  </div>
                </PreviewAltCard>
              </Col>

              <Col md="4">
                <PreviewAltCard className="card-full">
                  <div className="card-title-group align-start mb-0">
                    <div className="card-title">
                      <h6 className="subtitle">Accounts Payables (Outstanding)</h6>
                    </div>
                    <div className="card-tools" style={{ display: "flex", gap: "8px" }}>
                      <a
                        href="#"
                        style={{
                          marginLeft: "auto",
                          textAlign: "right",
                          // fontSize: "14px",
                          cursor: "pointer",
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          setAccountsPayablesDrawerVisible(true);
                        }}
                      >
                        View Details
                      </a>
                      <TooltipComponent
                        iconClass="card-hint"
                        icon="help-fill"
                        direction="left"
                        id="invest-balance"
                        text="Payable Amount"
                      ></TooltipComponent>
                    </div>
                  </div>
                  <div className="card-amount">
                    <span className="amount">
                      {formatCurrency2(
                        topWidgetsData?.paymentsWidget?.total_payable_value ||
                          0,
                        "INR"
                      )}
                      <Tooltip
                        title={formatCurrency(
                          topWidgetsData?.paymentsWidget?.total_payable_value,
                          "INR"
                        )}
                      >
                        <InfoCircleOutlined
                          style={{ marginLeft: 8, fontSize: 12 }}
                        />
                      </Tooltip>
                    </span>
                    <span className="m-1">
                      ( {topWidgetsData?.paymentsWidget?.payablePercentage} % ){" "}
                    </span>
                  </div>
                  <div className="invest-data">
                    <div className="invest-data-amount g-2">
                      <div className="invest-data-history">
                        <div className="title">This Month</div>
                        <div className="amount">
                          {/* 2,940.59 <span className="currency currency-usd">INR</span> */}
                          {formatCurrency2(
                            topWidgetsData?.paymentsWidget?.this_monthPayable ||
                              0,
                            "INR"
                          )}
                          <Tooltip
                            title={formatCurrency(
                              topWidgetsData?.paymentsWidget?.this_monthPayable,
                              "INR"
                            )}
                          >
                            <InfoCircleOutlined
                              style={{ marginLeft: 8, fontSize: 12 }}
                            />
                          </Tooltip>
                        </div>
                      </div>
                      <div className="invest-data-history">
                        <div className="title">This Week</div>
                        <div className="amount">
                          {/* 1,259.28 <span className="currency currency-usd">INR</span> */}
                          {formatCurrency2(
                            topWidgetsData?.paymentsWidget?.this_weekPayable ||
                              0,
                            "INR"
                          )}
                          <Tooltip
                            title={formatCurrency(
                              topWidgetsData?.paymentsWidget?.this_weekPayable,
                              "INR"
                            )}
                          >
                            <InfoCircleOutlined
                              style={{ marginLeft: 8, fontSize: 12 }}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                    <div className="invest-data-ck">
                      <BalanceBarChart
                        balanceBarChartData={
                          topWidgetsData?.paymentsWidget?.data
                        }
                      />
                    </div>
                  </div>
                </PreviewAltCard>
              </Col>

              {/* Rest of the widgets */}
              <Col md="6">
                <PreviewAltCard className="card-full">
                  <PRSummaryWidget
                    prSummaryData={purchaseRequestSummaryData}
                    startDate={convertToDates(dateRange).from}
                    endDate={convertToDates(dateRange).to}
                    siteId={site}
                  ></PRSummaryWidget>
                </PreviewAltCard>
              </Col>

              <Col md="6">
                <PreviewAltCard className="card-full">
                  <TopPurchaseCategoriesWidget
                    topPurchaseCategoriesData={topPurchaseCategoriesData}
                    site={site}
                    contractor={contractor}
                    contractorOptions={contractorOptions}
                    siteOptions={siteOptions}
                  ></TopPurchaseCategoriesWidget>
                </PreviewAltCard>
              </Col>

              <Col md="6">
                <PreviewAltCard className="card-full">
                  <TopSupplierSavingsWidget
                    dates={convertToDates(dateRange)}
                    sites={site}
                    contractor={contractor}
                    categoryOptions={categoryOptions}
                  ></TopSupplierSavingsWidget>
                </PreviewAltCard>
              </Col>
              <Col md="6">
                <PreviewAltCard className="card-full">
                  <BudgetOverRunWidget
                    dates={convertToDates(dateRange)}
                    sites={site}
                    contractor={contractor}
                    categoryOptions={categoryOptions}
                  ></BudgetOverRunWidget>
                </PreviewAltCard>
              </Col>
              <Col xl="12">
                <PreviewAltCard className="card-full">
                  <P2PWidget
                    procureToPayCycleData={procureToPayCycleData}
                    site={site}
                    contractor={contractor}
                    dateRange={convertToDates(dateRange)}
                  ></P2PWidget>
                </PreviewAltCard>
              </Col>
            </Row>
          </Block>
        )}
        <PurchaseOrdersDrawer
          visible={purchaseOrdersDrawerVisible}
          onClose={() => setPurchaseOrdersDrawerVisible(false)}
          site={site}
          contractor={contractor}
          dateRange={convertToDates(dateRange)}
          categoryId={""}
        />
        <InvoicesDrawer
          visible={invoicesDrawerVisible}
          onClose={() => setInvoicesDrawerVisible(false)}
          site={site}
          contractor={contractor}
          dateRange={convertToDates(dateRange)}
          categoryId={""}
        />
        <AccountsPayablesDrawer
          visible={accountsPayablesDrawerVisible}
          onClose={() => setAccountsPayablesDrawerVisible(false)}
          site={site}
          contractor={contractor}
          dateRange={convertToDates(dateRange)}
          categoryId={""}
        />
      </Content>
    </React.Fragment>
  );
};

export default InvestHomePage;
